import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageLayout } from "../../../components/modern";
import { useAuth } from "../../../contexts/AuthContext";
import { adminApi } from "../../../utils/api";
import { showErrorToast, showSuccessToast } from "../../../utils/toastNotifications";
import {
  ORDER_STATUS_VALUES,
  STATUS_APPROVED,
  STATUS_DELIVERED,
  STATUS_REFUNDED,
  isActiveStatus,
  isCancelledStatus,
  isFinalStatus,
  isRejectedStatus,
  money,
  orderTypeLabel,
  statusBadgeClass,
  statusLabel
} from "../orderGroupShared";
import type { OrderGroupStatusCount } from "../orderGroupShared";
import type { OrderGroupKind } from "../orderGroupKinds";
import { OrderGroupItemEditor } from "./OrderGroupItemEditor";
import "./OrderGroupDetails.scss";

interface OrderGroupItem {
  id: number;
  orderType: number;
  number?: string | null;
  link?: string | null;
  merchantName?: string | null;
  quantity: number;
  orderPrice: number;
  merchantDeliveryFee: number;
  tax: number;
  taxProfileName?: string | null;
  taxRuleValue?: number | null;
  taxRuleType?: number | null;
  shippingInsurance: number;
  paymentMethodFee: number;
  chosenWeight: number;
  isDangerous: boolean;
  originCountryName?: string | null;
  orderStatus: number;
  createdAt: string;
}

interface OrderGroupActivity {
  id: number;
  itemId: number;
  orderType: number;
  itemNumber?: string | null;
  actionDate: string;
  actionSummary?: string | null;
  status: number;
  isGroupEntry?: boolean;
  userName?: string | null;
}

interface OrderGroupInvoice {
  id: number;
  invoiceNumber?: string | null;
  amount: number;
  lockedAmountIqd?: number | null;
  isCollected: boolean;
  isReceived: boolean;
  isRefunded: boolean;
  refundAmount?: number | null;
  refundStatus?: number | null;
  refundCompletedAmount?: number | null;
  refundPendingAmount?: number;
  refundProcessedAt?: string | null;
  refundProcessedByName?: string | null;
  refundDecisionNote?: string | null;
  refundNote?: string | null;
  createdAt: string;
  paymentGatewayName?: string | null;
}

interface OrderGroupDetailsModel {
  id: number;
  orderType: number;
  originCountryId?: number | null;
  destinationCountryId?: number | null;
  cityId?: number | null;
  originCountryName?: string | null;
  destinationCountryName?: string | null;
  userName?: string | null;
  itemCount: number;
  createdAt: string;
  groupTotal: number;
  paidInvoiceTotal?: number;
  remainingAmount: number;
  pendingRefundTotal: number;
  statusCounts?: OrderGroupStatusCount[];
  items: OrderGroupItem[];
  invoices?: OrderGroupInvoice[];
  voucher?: {
    code?: string | null;
    voucherType?: number | null;
    rate?: number | null;
    maxAmount?: number | null;
    value: number;
  } | null;
  aggregate?: {
    activeItemCount: number;
    isDangerous: boolean;
    orderPriceTotal: number;
    merchantDeliveryFeeTotal: number;
    taxTotal: number;
    shippingInsuranceTotal: number;
    paymentMethodFeeTotal: number;
    chosenWeightTotal: number;
  };
  calculation?: {
    isReadyForCalculation: boolean;
    paymentType?: number | null;
    /** Share of the group total the first invoice of a partial payment collects. */
    partialPaymentPercent?: number | null;
    minimumPartialPaymentPercent?: number | null;
    firstPaymentAmount?: number | null;
    deliveryMode?: number | null;
    shippingCompanyId?: number | null;
    shippingCompanyName?: string | null;
    shippingCompanyPricePerLB?: number | null;
    manualShippingPricePerLB?: number | null;
    packaging?: number;
    shippingCost: number;
    shippingFuelCost: number;
    dgFees: number;
    localDeliveryFee: number;
    clearance: number;
    groupServicePrice: number;
    /** One service charge for the whole group, part of the group service price. */
    commission?: number;
    voucherTotal?: number;
    groupTotalPrice: number;
    validationMessages: string[];
    warnings?: string[];
  };
}

interface ShippingCompanyOption {
  id: number;
  name: string;
}

interface PaymentGatewayOption {
  id: number;
  name: string;
  localizedName?: string | null;
}

/** An action that needs a confirmation and may carry a note. */
interface PendingAction {
  title: string;
  danger?: boolean;
  run: (note: string) => Promise<void>;
}

type GroupTab = "calculation" | "items" | "invoices" | "activity" | "summary";
type ItemTab = "details" | "status" | "log" | "summary";
const itemKey = (item: Pick<OrderGroupItem, "orderType" | "id">) => `${item.orderType}-${item.id}`;
// Customer decisions and refunds have their own actions below.
const STATUS_CHOICES = ORDER_STATUS_VALUES.filter((status) => ![7, 8, STATUS_REFUNDED].includes(status));
const STATUS_PAYMENT_UNDER_COLLECTING = 9;
const STATUS_ON_HOLD = 11;
const STATUS_CANCELLED = 12;
const STATUS_ORDER_PURCHASED = 13;
const STATUS_REORDERED = 19;
const STATUS_SHIPPED = 20;
const REFUND_PENDING = 0;
const REFUND_COMPLETED = 1;
const REFUND_REJECTED = 2;

interface OrderGroupDetailsProps {
  /** The kind of group this page shows. */
  kind: OrderGroupKind;
}

export const OrderGroupDetails: React.FC<OrderGroupDetailsProps> = ({ kind }) => {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation("common");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // A link from the group list opens the page on one item: ?item=<type>-<id>.
  const requestedItemKey = searchParams.get("item");
  const { hasPermission } = useAuth();
  const [group, setGroup] = useState<OrderGroupDetailsModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [packaging, setPackaging] = useState("");
  const [manualPrice, setManualPrice] = useState("");
  const [originCountryId, setOriginCountryId] = useState("");
  const [destinationCountryId, setDestinationCountryId] = useState("");
  const [cityId, setCityId] = useState("");
  const [countries, setCountries] = useState<{ id: number; name: string }[]>([]);
  const [cities, setCities] = useState<{ id: number; name: string; countryId: number }[]>([]);
  const [shippingCompanyId, setShippingCompanyId] = useState("");
  const [paymentType, setPaymentType] = useState("");
  const [partialPercent, setPartialPercent] = useState("");
  const [deliveryMode, setDeliveryMode] = useState("");
  const [shippingCompanies, setShippingCompanies] = useState<ShippingCompanyOption[]>([]);
  const [savingCalculation, setSavingCalculation] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<GroupTab>(requestedItemKey ? "items" : "calculation");
  const [selectedItemKey, setSelectedItemKey] = useState<string | null>(requestedItemKey);
  const [activeItemTab, setActiveItemTab] = useState<ItemTab>("details");
  const [activity, setActivity] = useState<OrderGroupActivity[] | null>(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [actionNote, setActionNote] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [statusComment, setStatusComment] = useState("");
  const [statusReference, setStatusReference] = useState("");
  const [statusGatewayId, setStatusGatewayId] = useState("");
  const [paymentGateways, setPaymentGateways] = useState<PaymentGatewayOption[]>([]);
  const [voucherCode, setVoucherCode] = useState("");

  const canUpdate = hasPermission("Update Order Group");
  const canRefund = hasPermission("Refund Order Group");
  const canApproveRefund = hasPermission("Approve Refund Order Group");

  const fetchGroup = useCallback(async (showLoader = true) => {
    if (!id) return;
    if (showLoader) setLoading(true);
    try {
      const [response, calculationResponse] = await Promise.all([
        adminApi.call(`${kind.api}/${id}`, "GET"),
        adminApi.call(`${kind.api}/${id}/calculation`, "GET")
      ]);
      if (!response.success) {
        showErrorToast(response.error || t("orderGroups.messages.loadError"));
        return;
      }
      const nextGroup = response.data ? {
        ...response.data,
        calculation: calculationResponse.success ? calculationResponse.data : undefined
      } : null;
      setGroup(nextGroup);
      setOriginCountryId(nextGroup?.originCountryId?.toString() ?? "");
      setDestinationCountryId(nextGroup?.destinationCountryId?.toString() ?? "");
      setCityId(nextGroup?.cityId?.toString() ?? "");
      setActivity(null);
      setSelectedItemKey((current) => {
        if (nextGroup?.items?.some((item: OrderGroupItem) => itemKey(item) === current)) return current;
        return nextGroup?.items?.[0] ? itemKey(nextGroup.items[0]) : null;
      });
      if (calculationResponse.success && calculationResponse.data) {
        setPackaging(calculationResponse.data.packaging?.toString() || "0");
        setManualPrice(calculationResponse.data.manualShippingPricePerLB?.toString() || "");
        setShippingCompanyId(calculationResponse.data.shippingCompanyId?.toString() || "");
        setPaymentType(calculationResponse.data.paymentType?.toString() ?? "");
        setPartialPercent(calculationResponse.data.partialPaymentPercent?.toString() ?? "");
        setDeliveryMode(calculationResponse.data.deliveryMode?.toString() ?? "");
      }
    } catch (error) {
      console.error("Failed to load order group", error);
      showErrorToast(t("orderGroups.messages.loadError"));
    } finally {
      if (showLoader) setLoading(false);
    }
  }, [id, kind, t]);

  useEffect(() => {
    fetchGroup();
  }, [fetchGroup]);

  // Only active companies with prices for the chosen route can ship the group.
  useEffect(() => {
    // The route is known once the group has loaded.
    if (loading) return;
    let cancelled = false;
    const fetchShippingCompanies = async () => {
      const query = new URLSearchParams({ page: "1", pageSize: "10000", isActive: "true" });
      if (originCountryId) query.set("sourceCountryId", originCountryId);
      if (destinationCountryId) query.set("destinationCountryId", destinationCountryId);
      try {
        const response = await adminApi.call(`/shippingcompany?${query}`, "GET");
        if (response.success && !cancelled) {
          setShippingCompanies(response.data?.items || []);
        }
      } catch (error) {
        console.error("Failed to load shipping companies", error);
      }
    };

    fetchShippingCompanies();
    return () => {
      cancelled = true;
    };
  }, [loading, originCountryId, destinationCountryId]);

  // The countries and cities the route of the group is chosen from.
  useEffect(() => {
    const fetchRouteLists = async () => {
      try {
        const [countryResponse, cityResponse] = await Promise.all([
          adminApi.call("/country", "GET"),
          adminApi.call("/city?page=1&pageSize=1000", "GET")
        ]);
        if (countryResponse.success) setCountries((countryResponse.data?.items || []).filter((country: any) => !country.isSuspended));
        if (cityResponse.success) setCities(cityResponse.data?.items || []);
      } catch (error) {
        console.error("Failed to load countries and cities", error);
      }
    };

    fetchRouteLists();
  }, [i18n.language]);

  useEffect(() => {
    const fetchPaymentGateways = async () => {
      try {
        const response = await adminApi.call("/paymentgateway/active", "GET");
        if (response.success) {
          setPaymentGateways(response.data || []);
        }
      } catch (error) {
        console.error("Failed to load payment gateways", error);
      }
    };

    fetchPaymentGateways();
  }, [i18n.language]);

  useEffect(() => {
    if (!id || activity !== null || (activeTab !== "activity" && activeItemTab !== "log")) return;

    let cancelled = false;
    const fetchActivity = async () => {
      setActivityLoading(true);
      try {
        const response = await adminApi.call(`${kind.api}/${id}/activity`, "GET");
        if (!cancelled) {
          if (!response.success) {
            showErrorToast(response.error || t("orderGroups.messages.loadError"));
            setActivity([]);
          } else {
            setActivity(response.data || []);
          }
        }
      } catch (error) {
        console.error("Failed to load group activity", error);
        if (!cancelled) {
          showErrorToast(t("orderGroups.messages.loadError"));
          setActivity([]);
        }
      } finally {
        if (!cancelled) setActivityLoading(false);
      }
    };

    fetchActivity();
    return () => {
      cancelled = true;
    };
  }, [activeItemTab, activeTab, activity, id, kind, t]);

  /** Runs a write request, reports the outcome and reloads the group. */
  const submit = async (path: string, body: unknown, successMessage: string) => {
    setProcessing(true);
    try {
      const response = await adminApi.call(path, "POST", body);
      if (!response.success) {
        showErrorToast(response.error || t("orderGroups.messages.actionFailed"));
        return false;
      }
      showSuccessToast(successMessage);
      await fetchGroup(false);
      return true;
    } catch (error) {
      console.error("Order group action failed", error);
      showErrorToast(t("orderGroups.messages.actionFailed"));
      return false;
    } finally {
      setProcessing(false);
    }
  };

  const askConfirmation = (action: PendingAction) => {
    setActionNote("");
    setPendingAction(action);
  };

  const confirmPendingAction = async () => {
    if (!pendingAction) return;
    const action = pendingAction;
    setPendingAction(null);
    await action.run(actionNote.trim());
  };

  const recalculateGroup = async () => {
    if (!id) return;
    const packagingValue = packaging.trim() === "" ? null : Number(packaging);
    // An empty manual price clears a stored one (the API treats 0 as "clear").
    const manualPriceValue = manualPrice.trim() === "" ? 0 : Number(manualPrice);
    // The share of the first payment is fixed once the group is invoiced.
    const partialPercentValue = paymentType === "1" && !groupInvoiced && partialPercent.trim() !== ""
      ? Number(partialPercent)
      : null;
    if ((packagingValue !== null && (!Number.isFinite(packagingValue) || packagingValue < 0))
      || !Number.isFinite(manualPriceValue) || manualPriceValue < 0
      || (partialPercentValue !== null && (!Number.isFinite(partialPercentValue) || partialPercentValue <= 0))) {
      showErrorToast(t("orderGroups.messages.invalidNumber"));
      return;
    }

    setSavingCalculation(true);
    try {
      const response = await adminApi.call(`${kind.api}/${id}/calculation`, "PUT", {
        packaging: packagingValue,
        shippingCompanyId: shippingCompanyId ? Number(shippingCompanyId) : null,
        paymentType: paymentType === "" ? null : Number(paymentType),
        ...(partialPercentValue !== null ? { partialPaymentPercent: partialPercentValue } : {}),
        deliveryMode: deliveryMode === "" ? null : Number(deliveryMode),
        originCountryId: originCountryId ? Number(originCountryId) : null,
        destinationCountryId: destinationCountryId ? Number(destinationCountryId) : null,
        cityId: cityId ? Number(cityId) : null,
        manualShippingPricePerLB: manualPriceValue
      });
      if (!response.success) {
        showErrorToast(response.error || t("orderGroups.messages.actionFailed"));
        // Nothing was saved: show the terms the group really has again.
        await fetchGroup(false);
        return;
      }
      showSuccessToast(t("orderGroups.messages.recalculated"));
      await fetchGroup(false);
    } catch (error) {
      console.error("Failed to save group calculation", error);
      showErrorToast(t("orderGroups.messages.actionFailed"));
    } finally {
      setSavingCalculation(false);
    }
  };

  const refundItem = (item: OrderGroupItem) => askConfirmation({
    title: t("orderGroups.refund.confirmItem", { item: item.number || `#${item.id}` }),
    danger: true,
    run: async (note) => {
      await submit(`${kind.api}/${id}/refund-item`, { itemId: item.id, orderType: item.orderType, note },
        t("orderGroups.messages.itemRefunded"));
    }
  });

  const refundGroup = () => askConfirmation({
    title: t("orderGroups.refund.confirmGroup", { group: id }),
    danger: true,
    run: async (note) => {
      await submit(`${kind.api}/${id}/refund`, { note }, t("orderGroups.messages.groupRefunded"));
    }
  });

  const decideRefund = (invoice: OrderGroupInvoice, approve: boolean) => askConfirmation({
    title: approve
      ? t("orderGroups.refund.confirmApprove", { amount: money(invoice.refundPendingAmount) })
      : t("orderGroups.refund.confirmReject", { amount: money(invoice.refundPendingAmount) }),
    danger: !approve,
    run: async (note) => {
      await submit(
        `${kind.api}/${id}/invoices/${invoice.id}/refund/${approve ? "approve" : "reject"}`,
        { note },
        approve ? t("orderGroups.messages.refundApproved") : t("orderGroups.messages.refundRejected"));
    }
  });

  const itemController = (item: OrderGroupItem) => item.orderType === 1 ? "adminshipforme" : "adminorder";

  const groupInvoiced = (group?.invoices || []).length > 0;
  const hasUnpaidInvoice = (group?.invoices || []).some((invoice) => !invoice.isCollected && !invoice.isRefunded);

  /** Leaving "payment under collecting" or delivering an item collects the open group invoices. */
  const collectsPayment = (item: OrderGroupItem, status: number) => {
    // Delivering also issues the delivery invoice when it does not exist yet.
    if (status === STATUS_DELIVERED) return hasUnpaidInvoice || (group?.remainingAmount ?? 0) > 0;
    if (!hasUnpaidInvoice) return false;
    if (item.orderStatus !== STATUS_PAYMENT_UNDER_COLLECTING || status === STATUS_CANCELLED) return false;
    return item.orderType === 1 || status !== STATUS_ON_HOLD;
  };

  /** The number a status asks for, or null when it only takes an optional reference. */
  const requiredReferenceLabel = (item: OrderGroupItem, status: number) => {
    if (status === STATUS_ORDER_PURCHASED) return t("orderGroups.status.orderNumber");
    if (status === STATUS_SHIPPED) return t("orderGroups.status.shippingOrderNumber");
    if (status === STATUS_REORDERED) return t("orderGroups.status.reorderNumber");
    if (collectsPayment(item, status)) return t("orderGroups.status.receiptNumber");
    return null;
  };

  const changeItemStatus = async (item: OrderGroupItem) => {
    if (newStatus === "") return;
    const status = Number(newStatus);
    const changed = await submit(`/${itemController(item)}/updatestatus`, {
      orderId: item.id,
      orderStatus: status,
      comment: statusComment.trim() || null,
      extraField: statusReference.trim() || null,
      ...(collectsPayment(item, status) ? { paymentGatewayId: statusGatewayId ? Number(statusGatewayId) : null } : {})
    }, t("orderGroups.messages.statusChanged"));
    if (changed) {
      setNewStatus("");
      setStatusComment("");
      setStatusReference("");
      setStatusGatewayId("");
    }
  };

  const redeemVoucher = async (item: OrderGroupItem) => {
    const applied = await submit(`/${itemController(item)}/redeemvoucher`, { orderId: item.id, voucherCode: voucherCode.trim() },
      t("orderGroups.voucher.applied"));
    if (applied) setVoucherCode("");
  };

  const decideForCustomer = (item: OrderGroupItem, accept: boolean) => askConfirmation({
    title: accept
      ? t("orderGroups.status.confirmAccept", { item: item.number || `#${item.id}` })
      : t("orderGroups.status.confirmReject", { item: item.number || `#${item.id}` }),
    danger: !accept,
    run: async () => {
      await submit(`/${itemController(item)}/changecustomerstatus`, { orderId: item.id, orderStatus: accept ? 7 : 8 },
        t("orderGroups.messages.statusChanged"));
    }
  });

  const downloadInvoicePdf = async (invoiceId: number) => {
    if (!id) return;

    setDownloadingInvoiceId(invoiceId);
    try {
      const response = await adminApi.requestFile(`${kind.api}/${id}/invoices/${invoiceId}/pdf`, {
        method: "GET"
      });
      if (!response.success || !response.data) {
        showErrorToast(response.error || t("orderGroups.messages.actionFailed"));
        return;
      }

      const fileUrl = window.URL.createObjectURL(response.data.blob);
      const anchor = document.createElement("a");
      anchor.href = fileUrl;
      anchor.download = response.data.fileName || `group-invoice-${invoiceId}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(fileUrl);
    } catch (error) {
      console.error("Failed to download group invoice PDF", error);
      showErrorToast(t("orderGroups.messages.actionFailed"));
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  if (loading) {
    return <div className="text-center py-5">{t("orderGroups.loading")}</div>;
  }

  if (!group) {
    return (
      <PageLayout title={t(`orderGroups.kinds.${kind.labels}.title`)}>
        <div className="alert alert-danger">{t("orderGroups.notFound")}</div>
      </PageLayout>
    );
  }

  const selectedItem = group.items.find((item) => itemKey(item) === selectedItemKey) || group.items[0];
  const selectedItemActivity = selectedItem
    ? (activity || []).filter((entry) => !entry.isGroupEntry && entry.itemId === selectedItem.id && entry.orderType === selectedItem.orderType)
    : [];
  const canRefundItem = (item: OrderGroupItem) =>
    canRefund && item.orderStatus !== STATUS_REFUNDED && item.orderStatus !== STATUS_DELIVERED;
  const canApproveForCustomer = (item: OrderGroupItem) =>
    item.orderStatus === STATUS_APPROVED
    && hasPermission(item.orderType === 1 ? "Customer Approval Ship For Me" : "Customer Approval Order");
  const canChangeStatus = (item: OrderGroupItem) =>
    hasPermission(item.orderType === 1 ? "Update Ship For Me" : "Update Order");
  const canRedeemVoucher = (item: OrderGroupItem) =>
    item.orderStatus === STATUS_APPROVED
    && hasPermission(item.orderType === 1 ? "Customer Voucher Redemption Ship For Me" : "Customer Voucher Redemption Order");
  const chosenStatus = newStatus === "" ? null : Number(newStatus);

  const invoiceBadge = (invoice: OrderGroupInvoice) => {
    const paid = invoice.isCollected || invoice.isReceived;
    if (invoice.isRefunded && !paid) return { className: "bg-secondary", label: t("orderGroups.invoices.closed") };
    if (paid) return { className: "bg-success", label: t("orderGroups.invoices.collected") };
    return { className: "bg-warning text-dark", label: t("orderGroups.invoices.pending") };
  };

  const refundStatusLabel = (invoice: OrderGroupInvoice) => {
    if (invoice.refundStatus === REFUND_PENDING) return { className: "bg-warning text-dark", label: t("orderGroups.refund.pending") };
    if (invoice.refundStatus === REFUND_COMPLETED) return { className: "bg-success", label: t("orderGroups.refund.completed") };
    if (invoice.refundStatus === REFUND_REJECTED) return { className: "bg-danger", label: t("orderGroups.refund.rejected") };
    return null;
  };

  const taxRuleText = (item: OrderGroupItem) => {
    if (item.taxRuleValue == null) return null;
    const rule = item.taxRuleType === 1 ? `${money(item.taxRuleValue)} USD` : `${item.taxRuleValue}%`;
    return item.taxProfileName ? `${item.taxProfileName} · ${rule}` : rule;
  };

  const tabs: { key: GroupTab; label: string }[] = [
    { key: "calculation", label: t("orderGroups.tabs.calculation") },
    { key: "items", label: t("orderGroups.tabs.items") },
    { key: "invoices", label: t("orderGroups.tabs.invoices") },
    { key: "activity", label: t("orderGroups.tabs.activity") },
    { key: "summary", label: t("orderGroups.tabs.summary") }
  ];
  const itemTabs: { key: ItemTab; label: string }[] = [
    { key: "details", label: t("orderGroups.itemTabs.details") },
    { key: "status", label: t("orderGroups.itemTabs.status") },
    { key: "log", label: t("orderGroups.itemTabs.log") },
    { key: "summary", label: t("orderGroups.itemTabs.summary") }
  ];

  return (
    <PageLayout
      title={t(`orderGroups.kinds.${kind.labels}.detailsTitle`, { group: group.id })}
      subtitle={t("orderGroups.detailsSubtitle")}
      breadcrumbs={[
        { path: kind.path, label: t(`orderGroups.kinds.${kind.labels}.title`) },
        { label: `#${group.id}` }
      ]}
      className={i18n.language === "ar" ? "rtl" : "ltr"}
    >
      {pendingAction && (
        <div className={`alert ${pendingAction.danger ? "alert-danger" : "alert-info"} mb-4`}>
          <strong className="d-block mb-2">{pendingAction.title}</strong>
          <label className="form-label mb-1" htmlFor="order-group-action-note">{t("orderGroups.noteOptional")}</label>
          <input
            id="order-group-action-note"
            type="text"
            className="form-control mb-3"
            value={actionNote}
            onChange={(event) => setActionNote(event.target.value)}
          />
          <button type="button" className={`btn btn-sm ${pendingAction.danger ? "btn-danger" : "btn-primary"} me-2`} onClick={confirmPendingAction} disabled={processing}>
            {t("orderGroups.actions.confirm")}
          </button>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setPendingAction(null)}>
            {t("orderGroups.actions.cancel")}
          </button>
        </div>
      )}

      <ul className="nav nav-tabs mb-4" role="tablist">
        {tabs.map((tab) => (
          <li className="nav-item" role="presentation" key={tab.key}>
            <button type="button" className={`nav-link ${activeTab === tab.key ? "active" : ""}`} onClick={() => setActiveTab(tab.key)}>
              {tab.label}
            </button>
          </li>
        ))}
      </ul>

      {activeTab === "summary" && (
        <>
          <div className="order-group-summary card shadow-sm border-0 mb-4">
            <div className="card-body row g-3">
              <div className="col-md-3"><span className="summary-label">{t("orderGroups.columns.type")}</span><strong>{orderTypeLabel(t, group.orderType)}</strong></div>
              <div className="col-md-3"><span className="summary-label">{t("orderGroups.columns.origin")}</span><strong>{group.originCountryName || "—"}</strong></div>
              <div className="col-md-3"><span className="summary-label">{t("orderGroups.columns.destination")}</span><strong>{group.destinationCountryName || "—"}</strong></div>
              <div className="col-md-3"><span className="summary-label">{t("orderGroups.columns.customer")}</span><strong>{group.userName || "—"}</strong></div>
              <div className="col-md-3"><span className="summary-label">{t("orderGroups.columns.total")}</span><strong>{money(group.groupTotal)}</strong></div>
              <div className="col-md-3"><span className="summary-label">{t("orderGroups.columns.remaining")}</span><strong>{money(group.remainingAmount)}</strong></div>
              <div className="col-md-3"><span className="summary-label">{t("orderGroups.refund.pendingTotal")}</span><strong className={group.pendingRefundTotal > 0 ? "text-danger" : ""}>{money(group.pendingRefundTotal)}</strong></div>
              <div className="col-md-3">
                <span className="summary-label">{t("orderGroups.columns.statuses")}</span>
                <div className="d-flex flex-wrap gap-1">
                  {(group.statusCounts || []).map((entry) => (
                    <span key={entry.status} className={`badge ${statusBadgeClass(entry.status)}`}>{entry.count} · {statusLabel(t, entry.status)}</span>
                  ))}
                </div>
              </div>
              {group.voucher && (
                <div className="col-12">
                  <span className="summary-label">{t("orderGroups.voucher.title")}</span>
                  <strong>
                    {group.voucher.code || "—"} · {group.voucher.voucherType === 1 ? `${money(group.voucher.rate)} USD` : `${group.voucher.rate}%`}
                    {" "}({t("orderGroups.voucher.discount")}: -{money(group.voucher.value)})
                  </strong>
                </div>
              )}
              {canRefund && (
                <div className="col-12 d-flex justify-content-end">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={refundGroup}
                    disabled={processing || !group.items.some((item) => isActiveStatus(item.orderStatus))}
                  >
                    {t("orderGroups.refund.wholeGroup")}
                  </button>
                </div>
              )}
            </div>
          </div>

          {group.aggregate && (
            <div className="order-group-summary card shadow-sm border-0 mb-4">
              <div className="card-header">
                <h2 className="h6 mb-0">{t("orderGroups.summary.itemTotals")}</h2>
                {!kind.chargesGoods && <small className="text-muted">{t("orderGroups.summary.serviceOnlyNote")}</small>}
              </div>
              <div className="card-body row g-3">
                <div className="col-md-3"><span className="summary-label">{t("orderGroups.summary.activeItems")}</span><strong>{group.aggregate.activeItemCount}</strong></div>
                <div className="col-md-3"><span className="summary-label">{t("orderGroups.summary.chosenWeight")}</span><strong>{group.aggregate.chosenWeightTotal}</strong></div>
                <div className="col-md-3"><span className="summary-label">{t(kind.chargesGoods ? "orderGroups.summary.orderPrice" : "orderGroups.summary.goodsValue")}</span><strong>{money(group.aggregate.orderPriceTotal)}</strong></div>
                {kind.chargesGoods && (
                  <div className="col-md-3"><span className="summary-label">{t("orderGroups.summary.merchantFees")}</span><strong>{money(group.aggregate.merchantDeliveryFeeTotal)}</strong></div>
                )}
                <div className="col-md-3"><span className="summary-label">{t("orderGroups.summary.tax")}</span><strong>{money(group.aggregate.taxTotal)}</strong></div>
                <div className="col-md-3"><span className="summary-label">{t("orderGroups.summary.insurance")}</span><strong>{money(group.aggregate.shippingInsuranceTotal)}</strong></div>
                {kind.chargesGoods && (
                  <div className="col-md-3"><span className="summary-label">{t("orderGroups.summary.paymentFees")}</span><strong>{money(group.aggregate.paymentMethodFeeTotal)}</strong></div>
                )}
                <div className="col-md-3"><span className="summary-label">{t("orderGroups.summary.dangerous")}</span><strong>{group.aggregate.isDangerous ? t("orderGroups.yes") : t("orderGroups.no")}</strong></div>
              </div>
            </div>
          )}
        </>
      )}

      {activeTab === "calculation" && (
        group.calculation ? (
          <div className="order-group-summary card shadow-sm border-0 mb-4">
            <div className="card-header">
              <h2 className="h6 mb-0">{t("orderGroups.calc.title")}</h2>
              <small className={group.calculation.isReadyForCalculation ? "text-success" : "text-warning"}>
                {group.calculation.isReadyForCalculation ? t("orderGroups.calc.ready") : t("orderGroups.calc.notReady")}
              </small>
            </div>
            <div className="card-body row g-3">
              <div className="col-md-3"><span className="summary-label">{t("orderGroups.calc.shippingCompany")}</span><strong>{group.calculation.shippingCompanyName || "—"}</strong></div>
              <div className="col-md-3"><span className="summary-label">{t("orderGroups.calc.pricePerLb")}</span><strong>{money(group.calculation.shippingCompanyPricePerLB)}</strong></div>
              <div className="col-md-3"><span className="summary-label">{t("orderGroups.calc.shippingCost")}</span><strong>{money(group.calculation.shippingCost)}</strong></div>
              <div className="col-md-3"><span className="summary-label">{t("orderGroups.calc.fuel")}</span><strong>{money(group.calculation.shippingFuelCost)}</strong></div>
              <div className="col-md-3"><span className="summary-label">{t("orderGroups.calc.dgFees")}</span><strong>{money(group.calculation.dgFees)}</strong></div>
              <div className="col-md-3"><span className="summary-label">{t("orderGroups.calc.localDelivery")}</span><strong>{money(group.calculation.localDeliveryFee)}</strong></div>
              <div className="col-md-3"><span className="summary-label">{t("orderGroups.calc.clearance")}</span><strong>{money(group.calculation.clearance)}</strong></div>
              {group.calculation.commission != null && (
                <div className="col-md-3"><span className="summary-label">{t("orderGroups.calc.commission")}</span><strong>{money(group.calculation.commission)}</strong></div>
              )}
              <div className="col-md-3"><span className="summary-label">{t("orderGroups.calc.servicePrice")}</span><strong>{money(group.calculation.groupServicePrice)}</strong></div>
              {(group.calculation.voucherTotal ?? 0) > 0 && (
                <div className="col-md-3"><span className="summary-label">{t("orderGroups.voucher.discount")}</span><strong>-{money(group.calculation.voucherTotal)}</strong></div>
              )}
              <div className="col-md-3"><span className="summary-label">{t("orderGroups.calc.total")}</span><strong>{money(group.calculation.groupTotalPrice)}</strong></div>
              {group.calculation.paymentType === 1 && group.calculation.firstPaymentAmount != null && (
                <>
                  <div className="col-md-3">
                    <span className="summary-label">{t("orderGroups.calc.firstPayment", { percent: group.calculation.partialPaymentPercent })}</span>
                    <strong>{money(group.calculation.firstPaymentAmount)}</strong>
                  </div>
                  <div className="col-md-3">
                    <span className="summary-label">{t("orderGroups.calc.paidAtDelivery")}</span>
                    <strong>{money(group.calculation.groupTotalPrice - group.calculation.firstPaymentAmount)}</strong>
                  </div>
                </>
              )}
              {canUpdate && (
                <>
                  {/* The terms start on their own row, under the amounts. */}
                  <div className="col-12"><hr className="my-1" /></div>
                  <div className="col-md-4">
                    <label className="summary-label" htmlFor="group-origin-country">{t("orderGroups.calc.originCountry")}</label>
                    <select
                      id="group-origin-country"
                      className="form-select form-select-sm"
                      value={originCountryId}
                      onChange={(event) => setOriginCountryId(event.target.value)}
                    >
                      <option value="">{t("orderGroups.calc.chooseCountry")}</option>
                      {countries.map((country) => (
                        <option key={country.id} value={country.id}>{country.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="summary-label" htmlFor="group-destination-country">{t("orderGroups.calc.destinationCountry")}</label>
                    <select
                      id="group-destination-country"
                      className="form-select form-select-sm"
                      value={destinationCountryId}
                      onChange={(event) => {
                        // A city belongs to one country, so a new destination needs a new city.
                        setDestinationCountryId(event.target.value);
                        setCityId("");
                      }}
                    >
                      <option value="">{t("orderGroups.calc.chooseCountry")}</option>
                      {countries.map((country) => (
                        <option key={country.id} value={country.id}>{country.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="summary-label" htmlFor="group-city">{t("orderGroups.calc.city")}</label>
                    <select
                      id="group-city"
                      className="form-select form-select-sm"
                      value={cityId}
                      onChange={(event) => setCityId(event.target.value)}
                    >
                      <option value="">{t("orderGroups.calc.chooseCity")}</option>
                      {cities.filter((city) => String(city.countryId) === destinationCountryId).map((city) => (
                        <option key={city.id} value={city.id}>{city.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="summary-label" htmlFor="group-payment-type">{t("orderGroups.calc.paymentType")}</label>
                    <select
                      id="group-payment-type"
                      className="form-select form-select-sm"
                      value={paymentType}
                      onChange={(event) => setPaymentType(event.target.value)}
                    >
                      <option value="">{t("orderGroups.calc.choosePaymentType")}</option>
                      <option value="0">{t("orders:paymentTypes.0")}</option>
                      <option value="1">{t("orders:paymentTypes.1")}</option>
                      <option value="2">{t("orders:paymentTypes.2")}</option>
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="summary-label" htmlFor="group-delivery-mode">{t("orderGroups.calc.deliveryMode")}</label>
                    <select
                      id="group-delivery-mode"
                      className="form-select form-select-sm"
                      value={deliveryMode}
                      onChange={(event) => setDeliveryMode(event.target.value)}
                    >
                      <option value="">{t("orderGroups.calc.chooseDeliveryMode")}</option>
                      <option value="0">{t("orders:deliveryModes.0")}</option>
                      <option value="1">{t("orders:deliveryModes.1")}</option>
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="summary-label" htmlFor="group-shipping-company">{t("orderGroups.calc.shippingCompany")}</label>
                    <select
                      id="group-shipping-company"
                      className="form-select form-select-sm"
                      value={shippingCompanyId}
                      onChange={(event) => setShippingCompanyId(event.target.value)}
                    >
                      <option value="">{t("orderGroups.calc.chooseShippingCompany")}</option>
                      {/* The saved company stays visible even when it has no prices for the route. */}
                      {shippingCompanyId && !shippingCompanies.some((company) => String(company.id) === shippingCompanyId) && (
                        <option value={shippingCompanyId}>
                          {String(group.calculation.shippingCompanyId) === shippingCompanyId ? group.calculation.shippingCompanyName : `#${shippingCompanyId}`}
                        </option>
                      )}
                      {shippingCompanies.map((company) => (
                        <option key={company.id} value={company.id}>{company.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="summary-label" htmlFor="group-manual-price">{t("orderGroups.calc.manualPrice")}</label>
                    <input
                      id="group-manual-price"
                      type="number"
                      min="0"
                      step="0.01"
                      className="form-control form-control-sm"
                      value={manualPrice}
                      placeholder={t("orderGroups.calc.manualPricePlaceholder")}
                      onChange={(event) => setManualPrice(event.target.value)}
                    />
                  </div>
                  {paymentType === "1" && (
                    <div className="col-md-3">
                      <label className="summary-label" htmlFor="group-partial-percent">{t("orderGroups.calc.partialPercent")}</label>
                      <input
                        id="group-partial-percent"
                        type="number"
                        min={group.calculation.minimumPartialPaymentPercent ?? 0}
                        max="100"
                        step="0.01"
                        className="form-control form-control-sm"
                        value={partialPercent}
                        disabled={groupInvoiced}
                        onChange={(event) => setPartialPercent(event.target.value)}
                      />
                      <small className="text-muted">
                        {groupInvoiced
                          ? t("orderGroups.calc.partialPercentFixed")
                          : group.calculation.minimumPartialPaymentPercent != null
                            ? t("orderGroups.calc.partialPercentHint", { min: group.calculation.minimumPartialPaymentPercent })
                            : null}
                      </small>
                    </div>
                  )}
                  <div className="col-md-5">
                    <label className="summary-label" htmlFor="group-packaging">{t("orderGroups.calc.packaging")}</label>
                    <div className="input-group input-group-sm">
                      <input
                        id="group-packaging"
                        type="number"
                        min="0"
                        step="0.01"
                        className="form-control"
                        value={packaging}
                        onChange={(event) => setPackaging(event.target.value)}
                      />
                      <button type="button" className="btn btn-primary" onClick={recalculateGroup} disabled={savingCalculation}>
                        {savingCalculation ? t("orderGroups.saving") : t("orderGroups.calc.save")}
                      </button>
                    </div>
                  </div>
                </>
              )}
              {(group.calculation.warnings || []).length > 0 && (
                <div className="col-12 alert alert-info mb-0">{(group.calculation.warnings || []).join(" ")}</div>
              )}
              {group.calculation.validationMessages.length > 0 && (
                <div className="col-12 alert alert-warning mb-0">{group.calculation.validationMessages.join(" ")}</div>
              )}
            </div>
          </div>
        ) : (
          <div className="alert alert-warning">{t("orderGroups.calc.unavailable")}</div>
        )
      )}

      {activeTab === "invoices" && (
        <div className="card shadow-sm border-0 mb-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h2 className="h5 mb-0">{t("orderGroups.invoices.title")}</h2>
            <span className="badge bg-secondary">{group.invoices?.length || 0}</span>
          </div>
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>{t("orderGroups.columns.invoice")}</th>
                  <th>{t("orderGroups.columns.amount")}</th>
                  <th>{t("orderGroups.columns.method")}</th>
                  <th>{t("orderGroups.columns.status")}</th>
                  <th>{t("orderGroups.columns.refund")}</th>
                  <th>{t("orderGroups.columns.created")}</th>
                  <th className="text-end">{t("orderGroups.columns.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {(group.invoices || []).map((invoice) => {
                  const badge = invoiceBadge(invoice);
                  const refund = refundStatusLabel(invoice);
                  return (
                    <tr key={invoice.id}>
                      <td>{invoice.invoiceNumber || `#${invoice.id}`}</td>
                      <td>{money(invoice.amount)}</td>
                      <td>{invoice.paymentGatewayName || "—"}</td>
                      <td>
                        <span className={`badge ${badge.className}`}>{badge.label}</span>
                        {invoice.isReceived && <span className="badge bg-info ms-1">{t("orderGroups.invoices.received")}</span>}
                      </td>
                      <td>
                        {refund ? (
                          <>
                            <span className={`badge ${refund.className}`}>{refund.label}</span>
                            <small className="d-block mt-1">
                              {t("orderGroups.refund.amount")}: {money(invoice.refundAmount)}
                              {(invoice.refundCompletedAmount ?? 0) > 0 && ` · ${t("orderGroups.refund.completedAmount")}: ${money(invoice.refundCompletedAmount)}`}
                            </small>
                            {invoice.refundProcessedAt && (
                              <small className="d-block text-muted">
                                {invoice.refundProcessedByName || "—"} · {new Date(invoice.refundProcessedAt).toLocaleString()}
                                {invoice.refundDecisionNote ? ` · ${invoice.refundDecisionNote}` : ""}
                              </small>
                            )}
                          </>
                        ) : "—"}
                      </td>
                      <td>{invoice.createdAt ? new Date(invoice.createdAt).toLocaleString() : "—"}</td>
                      <td className="text-end">
                        {canApproveRefund && (invoice.refundPendingAmount ?? 0) > 0 && (
                          <>
                            <button type="button" className="btn btn-sm btn-success me-2" onClick={() => decideRefund(invoice, true)} disabled={processing}>
                              {t("orderGroups.refund.approve")}
                            </button>
                            <button type="button" className="btn btn-sm btn-outline-danger me-2" onClick={() => decideRefund(invoice, false)} disabled={processing}>
                              {t("orderGroups.refund.reject")}
                            </button>
                          </>
                        )}
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => downloadInvoicePdf(invoice.id)}
                          disabled={downloadingInvoiceId === invoice.id}
                        >
                          {t("orderGroups.invoices.downloadPdf")}
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {(!group.invoices || group.invoices.length === 0) && (
                  <tr><td colSpan={7} className="text-center py-4">{t("orderGroups.invoices.empty")}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === "items" && (
        <div className="card shadow-sm border-0">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h2 className="h5 mb-0">{t("orderGroups.items")}</h2>
            <span className="badge bg-primary">{group.itemCount}</span>
          </div>
          {selectedItem && (
            <div className="card-body border-bottom">
              <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap mb-3">
                <h3 className="h6 mb-0">
                  {selectedItem.number || `#${selectedItem.id}`}
                  <span className={`badge ms-2 ${statusBadgeClass(selectedItem.orderStatus)}`}>{statusLabel(t, selectedItem.orderStatus)}</span>
                </h3>
                <select
                  className="form-select form-select-sm"
                  style={{ maxWidth: 280 }}
                  value={itemKey(selectedItem)}
                  onChange={(event) => {
                    setSelectedItemKey(event.target.value);
                    setActiveItemTab("details");
                  }}
                >
                  {group.items.map((item) => (
                    <option key={itemKey(item)} value={itemKey(item)}>
                      {item.number || `#${item.id}`} · {orderTypeLabel(t, item.orderType)}
                    </option>
                  ))}
                </select>
              </div>
              <ul className="nav nav-pills mb-3" role="tablist">
                {itemTabs.map((tab) => (
                  <li className="nav-item" role="presentation" key={tab.key}>
                    <button type="button" className={`nav-link ${activeItemTab === tab.key ? "active" : ""}`} onClick={() => setActiveItemTab(tab.key)}>
                      {tab.label}
                    </button>
                  </li>
                ))}
              </ul>
              {activeItemTab === "details" && (
                <div className="row g-3">
                  <div className="col-md-3"><span className="summary-label">{t("orderGroups.columns.number")}</span><strong>{selectedItem.number || `#${selectedItem.id}`}</strong></div>
                  <div className="col-md-3"><span className="summary-label">{t("orderGroups.columns.type")}</span><strong>{orderTypeLabel(t, selectedItem.orderType)}</strong></div>
                  <div className="col-md-3"><span className="summary-label">{t("orderGroups.columns.merchant")}</span><strong>{selectedItem.merchantName || "—"}</strong></div>
                  <div className="col-md-3"><span className="summary-label">{t("orderGroups.columns.origin")}</span><strong>{selectedItem.originCountryName || "—"}</strong></div>
                  <div className="col-12">
                    <OrderGroupItemEditor
                      key={itemKey(selectedItem)}
                      itemId={selectedItem.id}
                      orderType={selectedItem.orderType}
                      canEdit={canChangeStatus(selectedItem)}
                      paymentType={group.calculation?.paymentType ?? null}
                      shippingCompanyId={group.calculation?.shippingCompanyId ?? null}
                      deliveryMode={group.calculation?.deliveryMode ?? null}
                      originCountryId={group.originCountryId ?? null}
                      destinationCountryId={group.destinationCountryId ?? null}
                      cityId={group.cityId ?? null}
                      pricePerLB={group.calculation?.manualShippingPricePerLB || null}
                      onChanged={() => fetchGroup(false)}
                    />
                  </div>
                </div>
              )}
              {activeItemTab === "status" && (
                <div className="row g-3">
                  <div className="col-12">
                    <small className="text-muted">{t("orderGroups.status.note")}</small>
                  </div>
                  {isRejectedStatus(selectedItem.orderStatus) && (
                    <div className="col-12 alert alert-secondary mb-0">{t("orderGroups.status.rejectedFinal")}</div>
                  )}
                  {isCancelledStatus(selectedItem.orderStatus) && (
                    <div className="col-12 alert alert-secondary mb-0">{t("orderGroups.status.cancelledFinal")}</div>
                  )}
                  {canApproveForCustomer(selectedItem) && (
                    <div className="col-12">
                      <button type="button" className="btn btn-sm btn-success me-2" onClick={() => decideForCustomer(selectedItem, true)} disabled={processing}>
                        {t("orderGroups.status.acceptForCustomer")}
                      </button>
                      <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => decideForCustomer(selectedItem, false)} disabled={processing}>
                        {t("orderGroups.status.rejectForCustomer")}
                      </button>
                    </div>
                  )}
                  {canRedeemVoucher(selectedItem) && (
                    <div className="col-md-6">
                      <label className="summary-label" htmlFor="item-voucher-code">{t("orderGroups.voucher.code")}</label>
                      <div className="input-group input-group-sm">
                        <input id="item-voucher-code" type="text" className="form-control" value={voucherCode} onChange={(event) => setVoucherCode(event.target.value)} />
                        <button type="button" className="btn btn-outline-primary" onClick={() => redeemVoucher(selectedItem)} disabled={processing || voucherCode.trim() === ""}>
                          {t("orderGroups.voucher.apply")}
                        </button>
                      </div>
                      <small className="text-muted">
                        {group.voucher?.code ? t("orderGroups.voucher.current", { code: group.voucher.code }) : t("orderGroups.voucher.groupNote")}
                      </small>
                    </div>
                  )}
                  {canChangeStatus(selectedItem) && (
                    <>
                      <div className="col-md-4">
                        <label className="summary-label" htmlFor="item-new-status">{t("orderGroups.status.newStatus")}</label>
                        <select id="item-new-status" className="form-select form-select-sm" value={newStatus} onChange={(event) => setNewStatus(event.target.value)}>
                          <option value="">{t("orderGroups.status.choose")}</option>
                          {STATUS_CHOICES
                            .filter((status) => status !== selectedItem.orderStatus)
                            .filter((status) => !(isFinalStatus(selectedItem.orderStatus) && isActiveStatus(status)))
                            .map((status) => (
                              <option key={status} value={status}>{statusLabel(t, status)}</option>
                            ))}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="summary-label" htmlFor="item-status-comment">{t("orderGroups.status.comment")}</label>
                        <input id="item-status-comment" type="text" className="form-control form-control-sm" value={statusComment} onChange={(event) => setStatusComment(event.target.value)} />
                      </div>
                      <div className="col-md-4">
                        <label className="summary-label" htmlFor="item-status-reference">
                          {(chosenStatus !== null && requiredReferenceLabel(selectedItem, chosenStatus)) || t("orderGroups.status.reference")}
                          {chosenStatus !== null && requiredReferenceLabel(selectedItem, chosenStatus) && <span className="text-danger"> *</span>}
                        </label>
                        <div className="input-group input-group-sm">
                          <input id="item-status-reference" type="text" className="form-control" value={statusReference} onChange={(event) => setStatusReference(event.target.value)} />
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => changeItemStatus(selectedItem)}
                            disabled={processing || chosenStatus === null
                              || (requiredReferenceLabel(selectedItem, chosenStatus) !== null && statusReference.trim() === "")}
                          >
                            {t("orderGroups.status.apply")}
                          </button>
                        </div>
                      </div>
                      {chosenStatus !== null && collectsPayment(selectedItem, chosenStatus) && (
                        <div className="col-md-4">
                          <label className="summary-label" htmlFor="item-status-gateway">{t("orderGroups.status.paymentGateway")}</label>
                          <select id="item-status-gateway" className="form-select form-select-sm" value={statusGatewayId} onChange={(event) => setStatusGatewayId(event.target.value)}>
                            <option value="">{t("orderGroups.status.chooseGateway")}</option>
                            {paymentGateways.map((gateway) => (
                              <option key={gateway.id} value={gateway.id}>{gateway.localizedName || gateway.name}</option>
                            ))}
                          </select>
                          <small className="text-muted">{t("orderGroups.status.collectNote")}</small>
                        </div>
                      )}
                    </>
                  )}
                  {canRefundItem(selectedItem) && (
                    <div className="col-12">
                      <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => refundItem(selectedItem)} disabled={processing}>
                        {t("orderGroups.refund.item")}
                      </button>
                    </div>
                  )}
                </div>
              )}
              {activeItemTab === "log" && (
                activityLoading ? <div className="text-muted">{t("orderGroups.loading")}</div> : selectedItemActivity.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-sm align-middle mb-0">
                      <thead><tr><th>{t("orderGroups.columns.date")}</th><th>{t("orderGroups.columns.summary")}</th><th>{t("orderGroups.columns.status")}</th></tr></thead>
                      <tbody>{selectedItemActivity.map((entry) => (
                        <tr key={`${entry.orderType}-${entry.id}`}>
                          <td>{entry.actionDate ? new Date(entry.actionDate).toLocaleString() : "—"}</td>
                          <td>{entry.actionSummary || "—"}</td>
                          <td><span className={`badge ${statusBadgeClass(entry.status)}`}>{statusLabel(t, entry.status)}</span></td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                ) : <div className="text-muted">{t("orderGroups.activity.emptyItem")}</div>
              )}
              {activeItemTab === "summary" && (
                <div className="row g-3">
                  <div className="col-md-3"><span className="summary-label">{t("orderGroups.columns.quantity")}</span><strong>{selectedItem.quantity}</strong></div>
                  <div className="col-md-3"><span className="summary-label">{t(kind.chargesGoods ? "orderGroups.summary.orderPrice" : "orderGroups.summary.goodsValue")}</span><strong>{money(selectedItem.orderPrice)}</strong></div>
                  {kind.chargesGoods && (
                    <div className="col-md-3"><span className="summary-label">{t("orderGroups.summary.merchantFees")}</span><strong>{money(selectedItem.merchantDeliveryFee)}</strong></div>
                  )}
                  <div className="col-md-3">
                    <span className="summary-label">{t("orderGroups.summary.tax")}</span>
                    <strong>{money(selectedItem.tax)}</strong>
                    {taxRuleText(selectedItem) && <small className="d-block text-muted">{taxRuleText(selectedItem)}</small>}
                  </div>
                  <div className="col-md-3"><span className="summary-label">{t("orderGroups.summary.insurance")}</span><strong>{money(selectedItem.shippingInsurance)}</strong></div>
                  {kind.chargesGoods && (
                    <div className="col-md-3"><span className="summary-label">{t("orderGroups.summary.paymentFees")}</span><strong>{money(selectedItem.paymentMethodFee)}</strong></div>
                  )}
                  <div className="col-md-3"><span className="summary-label">{t("orderGroups.summary.chosenWeight")}</span><strong>{money(selectedItem.chosenWeight)}</strong></div>
                  <div className="col-md-3"><span className="summary-label">{t("orderGroups.summary.dangerous")}</span><strong>{selectedItem.isDangerous ? t("orderGroups.yes") : t("orderGroups.no")}</strong></div>
                </div>
              )}
            </div>
          )}
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>{t("orderGroups.columns.number")}</th>
                  <th>{t("orderGroups.columns.type")}</th>
                  <th>{t("orderGroups.columns.merchant")}</th>
                  <th>{t("orderGroups.columns.origin")}</th>
                  <th>{t("orderGroups.columns.quantity")}</th>
                  <th>{t("orderGroups.columns.status")}</th>
                  <th className="text-end">{t("orderGroups.columns.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {group.items.map((item) => (
                  <tr key={itemKey(item)} className={selectedItem && itemKey(item) === itemKey(selectedItem) ? "table-active" : ""}>
                    <td>{item.number || `#${item.id}`}</td>
                    <td>{orderTypeLabel(t, item.orderType)}</td>
                    <td>{item.merchantName || "—"}</td>
                    <td>{item.originCountryName || "—"}</td>
                    <td>{item.quantity}</td>
                    <td><span className={`badge ${statusBadgeClass(item.orderStatus)}`}>{statusLabel(t, item.orderStatus)}</span></td>
                    <td className="text-end">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary me-2"
                        onClick={() => {
                          setSelectedItemKey(itemKey(item));
                          setActiveItemTab("status");
                        }}
                      >
                        {t("orderGroups.actions.manage")}
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => {
                          setSelectedItemKey(itemKey(item));
                          setActiveItemTab("details");
                        }}
                      >
                        {t("orderGroups.actions.viewItem")}
                      </button>
                    </td>
                  </tr>
                ))}
                {group.items.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-4">{t("orderGroups.emptyItems")}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === "activity" && (
        <div className="card shadow-sm border-0">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h2 className="h5 mb-0">{t("orderGroups.tabs.activity")}</h2>
            <span className="badge bg-secondary">{activity?.length || 0}</span>
          </div>
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>{t("orderGroups.columns.date")}</th>
                  <th>{t("orderGroups.columns.item")}</th>
                  <th>{t("orderGroups.columns.summary")}</th>
                  <th>{t("orderGroups.columns.user")}</th>
                  <th>{t("orderGroups.columns.status")}</th>
                </tr>
              </thead>
              <tbody>
                {activityLoading && <tr><td colSpan={5} className="text-center py-4">{t("orderGroups.loading")}</td></tr>}
                {!activityLoading && (activity || []).map((entry) => (
                  <tr key={`${entry.isGroupEntry ? "g" : entry.orderType}-${entry.id}`}>
                    <td>{entry.actionDate ? new Date(entry.actionDate).toLocaleString() : "—"}</td>
                    <td>
                      {entry.isGroupEntry
                        ? <span className="badge bg-primary">{t("orderGroups.activity.group")}</span>
                        : (entry.itemNumber || `#${entry.itemId}`)}
                    </td>
                    <td>{entry.actionSummary || "—"}</td>
                    <td>{entry.userName || "—"}</td>
                    <td>
                      {entry.isGroupEntry
                        ? "—"
                        : <span className={`badge ${statusBadgeClass(entry.status)}`}>{statusLabel(t, entry.status)}</span>}
                    </td>
                  </tr>
                ))}
                {!activityLoading && (activity || []).length === 0 && (
                  <tr><td colSpan={5} className="text-center py-4">{t("orderGroups.activity.empty")}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <button type="button" className="btn btn-link px-0 mt-3" onClick={() => navigate(-1)}>
        {t("orderGroups.actions.back")}
      </button>
    </PageLayout>
  );
};
